/**
 * 회사 기본 메일 꼬리말 만들기 — 「웍스에서 쓰던 서명 모양」을 문서 나무로 짓는다.
 * (v2.170.0 · 2026-09-14 · 사용자 요청 「웍스모바일에서 쓰던 서명을 포털에서도 그대로」)
 *
 * ## 왜 붙여넣기가 아니라 단추인가
 *
 * 웍스 서명은 **표 칸 안에 그림**을 넣어 짠 HTML 이다(남색 띠 안 로고 + 오른쪽 SNS 아이콘 넉 장).
 * 그 그림들은 `http://ai.esmplus.com/…` **바깥 주소**를 가리키는데, 편집기는 웹에서 온
 * `<img src="http…">` 를 **일부러 버린다**(남의 서버 주소를 문서에 싣지 않는다). 그래서 복사·붙여넣기로는
 * 표 껍데기만 들어오고 로고·아이콘이 사라진다 — 2026-09-14 에 사용자가 겪은 그 모양이다.
 * → 그림은 앱이 들고 있다가(`mail-signature-assets.ts`) 이 함수가 **바이트째** 문서에 넣는다.
 *
 * ## 무엇이 웍스와 다른가 (일부러 다르게 한 것)
 *
 * | | 웍스 | 여기 |
 * |---|---|---|
 * | 그림 | 바깥 주소를 불러온다 | **메일에 직접 싣는다**(cid) — 받는 쪽이 외부 그림을 막아도 보인다 |
 * | 표 폭 | 544px 고정 | `max-width:544px` — 좁은 화면·휴대폰에서 넘치지 않는다 |
 * | 칸 안 표 | 아이콘을 1줄×4칸 표로 | **표 없이** 아이콘 넉 장을 나란히 (서버가 펴서 담는 것과 같은 결과) |
 *
 * ## 두 부분으로 나뉜다 (v2.211.0 · 2026-09-28)
 *
 * 사용자: 「이름 뒤에 직급(황재상 부장) … 모든 직원들에게 각자 자기 이름과 전화번호, 이메일로 동일하게」
 *   → 「인사말 맨트는 공통적용 아니고, 꼬리말 중 회사정보 부분만 공통으로」.
 *
 * | | 어디에 | 누가 |
 * |---|---|---|
 * | 인사말(`buildDefaultGreeting`) | 마이페이지에 **저장** — 사람마다 다름 · 비워도 됨 | 각자 |
 * | 회사 정보 표(`buildCompanyBlock`) | **저장 안 함** — 메일 창을 열 때마다 직원 정보로 새로 짓는다 | 공통 |
 *
 * ⭐사람 정보(이름·직급·부서·휴대폰·메일)는 서버가 주는 **그 사람의 직원 정보**다(GET /mail/signature 의
 *   profile · 직급·휴대폰은 관리자가 사용자 관리에서 고친다). 그래서 직급이 바뀌거나 번호를 고치면 다음
 *   메일부터 바로 반영되고, 새 직원도 계정만 있으면 붙는다. 메일 창 안에서는 그 메일에 한해 고칠 수 있다.
 */

import type { RichNode } from './rich-doc';

/** 남색 띠 — 웍스 서명 실측값 rgb(27,20,100) */
const NAVY = '#1b1464';
/** 표 전체 폭(px) — 웍스 서명 실측값 */
const TABLE_W = 544;
/** 첫 줄 세 칸의 폭(px) — 합이 TABLE_W. 렌더러가 이 비율로 `<colgroup>` 을 만든다 */
const COL_W = [178, 206, 160];

/** 회사 고정 정보 — 바뀌면 여기 한 곳만 고친다 */
const COMPANY = {
  nameKo: '(주)에스엠시스템즈',
  nameEn: 'SM SYSTEMS CO., LTD',
  address: '(08504) 서울시 금천구 가산디지털2로 135, A동 1007호(가산동, 가산어반워크I )',
  tel: '070-8708-4770',
  fax: '070-8708-4775',
  homepage: 'http://www.smsys.co.kr/',
  facebook: 'https://www.facebook.com/SMSYSTEMS07/',
  instagram: 'https://www.instagram.com/smsystems07/',
  youtube: 'https://www.youtube.com/channel/UCPq_n9ucXSZNZzt_mG08xXg/',
};

/** 법적 고지문 — 웍스 서명 원문 그대로(237자). ⚠꼬리말 한 칸 상한 260자 안에 들어야 한다 */
const DISCLAIMER =
  '상기 메일은 지정된 수신인만을 위한 것이며, 부정경쟁 방지 및 영업비밀의 보호에 관한 법률을 포함하여 '
  + '관계 법령에 따라 보호 대상이 되는 영업 비밀, 기밀정보 등을 포함하고 있을 수 있습니다. '
  + '본 문서에 포함된 정보의 전부 또는 일부를 무단으로 제 3자에게 공개, 배포, 복사 또는 사용하는 것은 '
  + '엄격히 금지됩니다. 본 메일이 잘못 전송된 경우, 발신인 또는 당사자에게 알려주시고, '
  + '본 메일을 즉시 삭제하여 주시기 바랍니다.';

/** 꼬리말을 채울 사람 — 없는 값은 그 자리를 통째로 뺀다(빈 괄호·따로 노는 `|` 가 남지 않게) */
export interface SignatureProfile {
  name: string;
  /** 직급 (부장·과장…) */
  title?: string | null;
  /** 부서 (시스템사업부…) */
  dept?: string | null;
  email?: string | null;
  /** 휴대폰 */
  mobile?: string | null;
}

/* ── 작은 조립 도구 ── */

function text(s: string, marks?: RichNode[]): RichNode {
  return marks?.length ? ({ type: 'text', text: s, marks } as unknown as RichNode) : { type: 'text', text: s } as RichNode;
}
const bold = { type: 'bold' } as unknown as RichNode;
function color(hex: string): RichNode {
  return { type: 'textStyle', attrs: { color: hex } } as unknown as RichNode;
}
function link(href: string): RichNode {
  return { type: 'link', attrs: { href } } as unknown as RichNode;
}
function para(...content: RichNode[]): RichNode {
  return { type: 'paragraph', content } as RichNode;
}
/** 빈 줄 */
function blank(): RichNode {
  return { type: 'paragraph' } as RichNode;
}
/** 새 그림 — 번호가 아직 없으므로 바이트(dataUrl)로 넣는다. 저장할 때 서버가 번호를 매긴다 */
function image(dataUrl: string, wPx: number, alt: string, href?: string): RichNode {
  return {
    type: 'image',
    attrs: {
      imageId: 0, src: dataUrl, widthPct: 100, w: 0, h: 0, alt, wPx,
      ...(href ? { href } : {}),
    },
  } as unknown as RichNode;
}
function cell(
  content: RichNode[],
  opts: {
    colspan?: number; colwidth?: number[] | null; background?: string | null; fontSizePx?: number;
    vAlign?: 'middle'; keepWords?: true;
  } = {},
): RichNode {
  return {
    type: 'tableCell',
    attrs: {
      colspan: opts.colspan ?? 1,
      rowspan: 1,
      colwidth: opts.colwidth ?? null,
      background: opts.background ?? null,
      ...(opts.fontSizePx ? { fontSizePx: opts.fontSizePx } : {}),
      ...(opts.vAlign ? { vAlign: opts.vAlign } : {}),
      ...(opts.keepWords ? { keepWords: true } : {}),
    },
    content,
  } as unknown as RichNode;
}

/** 이름 — 비어 있으면 자리표 (서버는 늘 이름이나 계정 ID 를 준다) */
function nameOf(p: SignatureProfile): string {
  return p.name.trim() || '홍길동';
}

/**
 * 인사말(개인 부분)의 **기본 글** — 마이페이지 [기본 인사말 넣기] 가 편집기에 넣는다(v2.211.0).
 * 넣은 뒤에는 그냥 글이라 사람마다 고친다. 회사 정보 표와 달리 **공통이 아니다**(사용자 결정).
 * 문장은 예전 [웍스 서명 모양 넣기] 가 넣던 것 그대로 · 이름 뒤에 직급을 붙인다(사용자 「황재상 부장 으로」).
 */
export function buildDefaultGreeting(p: SignatureProfile): RichNode[] {
  const who = [nameOf(p), (p.title ?? '').trim()].filter(Boolean).join(' ');
  return [
    para(text(`안녕하세요. 에스엠시스템즈 ${who} 입니다.`)),
    blank(),
    para(text('추가로 궁금하신점 있으시면 언제든지 연락 부탁드립니다.')),
    para(text('감사합니다.')),
  ];
}

/**
 * 「회사 정보 표」(공통) — 남색 띠 · 회사 이름·주소·연락처 · 법적 고지문. **표 한 개**를 돌려준다.
 * 메일 창이 인사말 아래에 붙인다. 저장하지 않는다(그때그때 지은 것이 늘 최신 직원 정보다).
 *
 * ⚠`headerRow`·`headerCol` 을 **둘 다 꺼야 한다** — 기본값이 켬이라 그냥 두면 첫 줄이 가운데
 *   정렬·굵게가 되고 첫 칸에 회색 명암이 깔려 남색 띠와 따로 논다(회의록 v2.121.0 의 규칙).
 * ⭐이 표가 쓰는 글·그림은 서버 `MAIL_COMPANY_BLOCK_LIMITS` 안이어야 한다 — `check:richdoc ⑫` 가 잰다.
 * ⭐남색 띠 첫 칸 바탕색(NAVY)은 「이것이 회사 정보 표다」를 알아보는 표시로도 쓰인다(메일 창의 「이미
 *   들어 있나」 판정 · 마이그레이션). 바꾸면 그 두 곳도 함께 볼 것.
 */
export async function buildCompanyBlock(p: SignatureProfile): Promise<RichNode[]> {
  // 그림은 쓸 때만 받아 온다 — 14KB 가 모든 사람의 첫 화면에 따라붙을 이유가 없다
  const A = await import('./mail-signature-assets');

  const title = (p.title ?? '').trim();
  const dept = (p.dept ?? '').trim();
  const email = (p.email ?? '').trim();
  const mobile = (p.mobile ?? '').trim();

  /** 「황재상 부장 | 시스템사업부」 — 없는 조각은 빼고 구분자도 함께 뺀다 */
  const whoLeft = [nameOf(p), title].filter(Boolean).join(' ');
  const who = [whoLeft, dept].filter(Boolean).join(' | ');

  /* ── 연락처 줄 — 「T … F … M … E …」. 라벨은 굵은 남색, 값은 검정 · 휴대폰이 없으면 M 을 통째로 뺀다 ── */
  const contact: RichNode[] = [];
  const label = (s: string): RichNode => text(s, [bold, color(NAVY)]);
  contact.push(label('T '), text(`${COMPANY.tel}   `));
  contact.push(label('F '), text(`${COMPANY.fax}   `));
  if (mobile) contact.push(label('M '), text(`${mobile}   `));
  if (email) contact.push(label('E '), text(email, [link(`mailto:${email}`)]));

  return [
    {
      type: 'table',
      attrs: { headerRow: false, headerCol: false, maxWidthPx: TABLE_W },
      content: [
        /* ① 남색 띠 — 로고 · 이름 직급 | 부서 · SNS 아이콘
         *   세 칸 모두 **세로 가운데** — 표 기본(위)이면 띠 높이를 아이콘 칸이 정해 이름·부서가 위로 쏠린다
         *   (v2.210.2 · 사용자 「이름과 부서가 위로 좀 쏠려서… 가운데로」)
         *   이름 칸은 **낱말 단위 줄바꿈** — 폰에서 「시스템/사업부」가 아니라 「황재상 부장 |」/「시스템사업부」 (v2.211.0) */
        {
          type: 'tableRow',
          content: [
            cell([image(A.SM_LOGO_WHITE, 155, '에스엠시스템즈')],
              { colwidth: [COL_W[0]!], background: NAVY, vAlign: 'middle' }),
            cell([para(text(who, [bold, color('#ffffff')]))],
              { colwidth: [COL_W[1]!], background: NAVY, vAlign: 'middle', keepWords: true }),
            cell([
              image(A.ICON_WEB, 30, '홈페이지', COMPANY.homepage),
              image(A.ICON_FACEBOOK, 30, '페이스북', COMPANY.facebook),
              image(A.ICON_INSTAGRAM, 30, '인스타그램', COMPANY.instagram),
              image(A.ICON_YOUTUBE, 30, '유튜브', COMPANY.youtube),
            ], { colwidth: [COL_W[2]!], background: NAVY, vAlign: 'middle' }),
          ],
        } as unknown as RichNode,
        /* ② 회사 이름·주소·연락처 */
        {
          type: 'tableRow',
          content: [
            cell([
              para(text(`${COMPANY.nameKo} | ${COMPANY.nameEn}`, [bold, color(NAVY)])),
              para(text(COMPANY.address)),
              para(...contact),
            ], { colspan: 3 }),
          ],
        } as unknown as RichNode,
        /* ③ 법적 고지문 — 일부러 작은 글씨(웍스 원본 10px) */
        {
          type: 'tableRow',
          content: [
            cell([para(text(DISCLAIMER, [color(NAVY)]))], { colspan: 3, fontSizePx: 10 }),
          ],
        } as unknown as RichNode,
      ],
    } as unknown as RichNode,
  ];
}

/**
 * 이 표가 회사 정보 표인가 — 첫 줄 첫 칸 바탕이 남색. 메일 창의 「꼬리말이 이미 들어 있나」가 쓴다.
 * (저장된 문서에서 옛 표를 떼어 낸 마이그레이션 …_sig_mobile.sql 도 같은 표시로 알아봤다)
 */
export function isCompanyBlockTable(node: RichNode): boolean {
  if (node.type !== 'table') return false;
  const firstCell = (node as unknown as { content?: { content?: { attrs?: { background?: unknown } }[] }[] })
    .content?.[0]?.content?.[0];
  const bg = firstCell?.attrs?.background;
  return typeof bg === 'string' && bg.toLowerCase() === NAVY;
}
